import { useEffect, useState } from "react";
import { Loader2, Sparkles, Wand2, X } from "lucide-react";
import type { ProductCategory } from "@erc/shared";
import { authHeaders, getBackendUrl } from "../lib/http";

// Studio: tedarikcinin urun fotograflari (or. 3 rengi yan yana gosteren kare)
// tema referanslarina bakilarak yeniden uretilir — urun birebir korunur, yalniz
// arka plan / zemin / aksesuar / isik referanslara uyarlanir. HER FOTOGRAFIN KENDI
// referans seti var (1. foto: konsept + 2 ilham fotografi, 2. foto: baska konsept…).
// Kaynaklar: konsept kutuphanesi, urunun uretilmis kareleri, ilham fotograflari.

const abs = (u?: string | null) => (!u ? "" : /^(https?:|data:|blob:)/.test(u) ? u : `${getBackendUrl()}${u}`);
const MAX_REFS = 4;

export function RestyleProductPhotos({ catalogId, cat, concepts, shotUrls, model, onDone }: {
  catalogId: string;
  cat: ProductCategory;
  /** Konsept kutuphanesi (urun tipine uyanlar once) */
  concepts: Array<{ url: string; label: string }>;
  /** Urunun daha once uretilmis kareleri */
  shotUrls: string[];
  model?: string;
  /** Uretim bitti: galeri yeniden okunur */
  onDone: () => void;
}) {
  const photos = (cat.source?.images ?? []).slice(0, 12);
  // Urun fotografi -> kendi tema referanslari (sirali)
  const [refs, setRefs] = useState<Record<string, string[]>>({});
  const [active, setActive] = useState<string | null>(null);
  const [inspo, setInspo] = useState<string[] | null>(null);
  const [extra, setExtra] = useState("");
  // Yazili tema (AI onerisi): urun fotografi -> sahne cumlesi; referans yerine/ek
  const [themes, setThemes] = useState<Record<string, { label: string; prompt: string }>>({});
  const [ideas, setIdeas] = useState<Array<{ labelTr: string; prompt: string }> | null>(null);
  const [ideasBusy, setIdeasBusy] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Urun degisince secimler sifirlanir; ilham fotograflari urunun analizlerinden
  useEffect(() => {
    setRefs({}); setActive(null); setMsg(null); setInspo(null); setThemes({}); setIdeas(null);
    const q = new URLSearchParams();
    if (cat.source?.sku) q.set("sku", cat.source.sku);
    const ids = (cat.inspirations ?? []).map((i) => i.listingId).filter(Boolean);
    if (ids.length) q.set("listingIds", ids.join(","));
    // Ilham ilanlarinin kayitli fotograflari (analizi olmasa da) + ilham analizleri
    const own = (cat.inspirations ?? []).flatMap((i) => (i.images ?? []).slice(0, 6).map((u) => u.replace(/il_\d+xN/, "il_fullxfull")));
    if (![...q.keys()].length) { setInspo(own); return; }
    void fetch(`${getBackendUrl()}/api/analyses/inspiration-images?${q}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((j: { data?: Array<{ images: string[] }> }) => setInspo([...new Set([...own, ...(j.data ?? []).flatMap((g) => g.images.slice(0, 8))])].slice(0, 30)))
      .catch(() => setInspo(own));
  }, [cat]);

  const togglePhoto = (u: string) => {
    if (active === u) { setActive(null); return; }
    setActive(u);
    setRefs((m) => (m[u] ? m : { ...m, [u]: [] }));
  };
  const removePhoto = (u: string) => {
    setRefs((m) => { const n = { ...m }; delete n[u]; return n; });
    setThemes((m) => { const n = { ...m }; delete n[u]; return n; });
    if (active === u) setActive(null);
  };
  const toggleRef = (r: string) => {
    if (!active) return;
    setRefs((m) => {
      const cur = m[active] ?? [];
      return { ...m, [active]: cur.includes(r) ? cur.filter((x) => x !== r) : cur.length >= MAX_REFS ? cur : [...cur, r] };
    });
  };

  // Secilen her urun fotografi uretilir: referans ve tema yoksa sunucu urunun
  // uretilmis karelerini tema alir (konsept secmeden temaya uyarla)
  const jobs = photos.filter((p) => refs[p]);

  async function suggestThemes() {
    setIdeasBusy(true); setMsg(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/media/suggest-themes`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ catalogId, categoryId: cat.id, productImageUrl: active ?? photos[0], productName: cat.name }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error ?? `Öneri alınamadı (${r.status})`);
      setIdeas(j.data);
    } catch (e) { setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) }); } finally { setIdeasBusy(false); }
  }
  const toggleTheme = (t: { labelTr: string; prompt: string }) => {
    if (!active) return;
    setThemes((m) => (m[active]?.prompt === t.prompt ? (() => { const n = { ...m }; delete n[active]; return n; })() : { ...m, [active]: { label: t.labelTr, prompt: t.prompt } }));
  };
  async function run() {
    setMsg(null);
    let ok = 0;
    const errors: string[] = [];
    for (const u of jobs) {
      setBusy(u);
      try {
        const r = await fetch(`${getBackendUrl()}/api/media/restyle-product-photo`, {
          method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ catalogId, categoryId: cat.id, productImageUrl: u, styleImageUrls: refs[u], themePrompt: themes[u]?.prompt, extraPrompt: extra.trim() || undefined, model: model || undefined }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error ?? `Üretilemedi (${r.status})`);
        ok++;
      } catch (e) { errors.push(`${photos.indexOf(u) + 1}. foto: ${e instanceof Error ? e.message : String(e)}`); }
    }
    setBusy(null);
    if (ok) onDone();
    setMsg(errors.length
      ? { ok: ok > 0, text: `${ok} fotoğraf üretildi. ${errors.join(" · ")}` }
      : { ok: true, text: `${ok} fotoğraf temaya uyarlandı ve galeriye eklendi.` });
  }

  const activeRefs = active ? refs[active] ?? [] : [];
  const refRow = (label: string, items: Array<{ url: string; label?: string }>) => items.length > 0 && (
    <div>
      <p className="mb-1 text-xs text-gray-500">{label}</p>
      <div className="flex max-h-36 flex-wrap gap-1 overflow-y-auto">
        {items.map((it) => {
          const n = activeRefs.indexOf(it.url) + 1;
          return (
            <button key={it.url} type="button" onClick={() => toggleRef(it.url)} disabled={!active}
              title={active ? (it.label ? `${it.label} — referans ekle/çıkar` : "Referans ekle/çıkar") : "Önce bir ürün fotoğrafı seçin"}
              className={`relative h-12 w-12 overflow-hidden rounded-lg border-2 disabled:cursor-not-allowed disabled:opacity-50 ${n ? "border-indigo-600" : "border-gray-200 hover:border-gray-400"}`}>
              <img src={abs(it.url)} alt="" loading="lazy" className="h-full w-full object-cover" />
              {n > 0 && <span className="absolute right-0.5 top-0.5 rounded-full bg-indigo-600 px-1 text-[10px] font-bold leading-4 text-white">{n}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <details className="mb-3 rounded-lg border border-gray-200 bg-white p-3">
      <summary className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-gray-800">
        <Wand2 className="h-3.5 w-3.5 text-indigo-600" /> Ürün fotoğraflarını temaya uyarla
      </summary>
      <div className="mt-2 space-y-3">
        <p className="text-xs text-gray-500">
          Tedarikçi fotoğrafındaki ürün(ler) <b>birebir korunur</b> (adet, renkler, diziliş, yazı); yalnız arka plan, zemin, aksesuar ve ışık o fotoğrafa seçtiğin referanslara uyarlanır. Rozet/logo silinir.
        </p>
        <div>
          <p className="mb-1 text-xs font-medium text-gray-700">1 · Ürün fotoğrafına tıkla (aktif olur)</p>
          <div className="flex flex-wrap gap-1">
            {photos.map((u, i) => (
              <button key={u} type="button" onClick={() => togglePhoto(u)} aria-pressed={active === u} title={`${i + 1}. fotoğraf`}
                className={`relative h-14 w-14 overflow-hidden rounded-lg border-2 ${active === u ? "border-indigo-600 ring-2 ring-indigo-200" : refs[u] ? "border-indigo-300" : "border-gray-200 opacity-80 hover:opacity-100"}`}>
                <img src={u} alt="" className="h-full w-full object-cover" />
                <span className="absolute left-0.5 top-0.5 rounded bg-white/90 px-1 text-[10px] font-bold text-gray-800">{i + 1}</span>
                {(refs[u]?.length ?? 0) > 0 && <span className="absolute bottom-0.5 right-0.5 rounded-full bg-indigo-600 px-1 text-[10px] font-bold leading-4 text-white">{refs[u].length}</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-700">
            2 · {active ? `${photos.indexOf(active) + 1}. fotoğrafın referansları (en fazla ${MAX_REFS})` : "Referans seçmek için önce bir ürün fotoğrafı seç"}
          </p>
          {refRow("Konsept kütüphanesi", concepts)}
          {refRow("Üretilen kareler", shotUrls.slice(0, 24).map((url) => ({ url })))}
          {inspo === null
            ? <p className="flex items-center gap-1 text-xs text-gray-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> İlham fotoğrafları yükleniyor…</p>
            : refRow("İlham listesi fotoğrafları", inspo.map((url) => ({ url })))}

          {/* AI tema onerisi: gorsel referans yerine yazili sahne */}
          <div>
            <div className="mb-1 flex items-center gap-2">
              <p className="text-xs text-gray-500">AI tema önerisi (mevcut karelerden farklı)</p>
              <button type="button" onClick={() => void suggestThemes()} disabled={ideasBusy}
                className="ml-auto inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {ideasBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {ideas ? "Yeniden öner" : "AI tema öner"}
              </button>
            </div>
            {ideas && (
              <div className="flex flex-wrap gap-1">
                {ideas.map((t) => {
                  const on = !!active && themes[active]?.prompt === t.prompt;
                  return (
                    <button key={t.prompt} type="button" onClick={() => toggleTheme(t)} disabled={!active} title={t.prompt}
                      className={`rounded-full border px-2.5 py-1 text-xs disabled:opacity-50 ${on ? "border-indigo-600 bg-indigo-50 font-medium text-indigo-700" : "border-gray-200 text-gray-700 hover:border-gray-400"}`}>
                      {t.labelTr || t.prompt.slice(0, 40)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500">Referans ve tema seçmezsen, ürünün daha önce üretilen kareleri tema alınır.</p>
        </div>

        {/* Plan: her fotograf kendi referanslariyla */}
        {Object.keys(refs).length > 0 && (
          <div className="space-y-1.5 rounded-lg bg-gray-50 p-2">
            <p className="text-xs font-medium text-gray-700">Üretilecekler</p>
            {photos.filter((p) => refs[p]).map((p) => (
              <div key={p} className="flex items-center gap-1.5">
                <img src={p} alt="" className="h-9 w-9 shrink-0 rounded border border-gray-200 object-cover" />
                <span className="text-xs text-gray-500">←</span>
                {refs[p].map((r) => <img key={r} src={abs(r)} alt="" className="h-9 w-9 shrink-0 rounded border border-indigo-200 object-cover" />)}
                {themes[p] && <span className="truncate rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700" title={themes[p].prompt}>{themes[p].label || "AI tema"}</span>}
                {!refs[p].length && !themes[p] && <span className="text-xs text-gray-500">otomatik tema (üretilen karelere göre)</span>}
                {busy === p && <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />}
                <button type="button" onClick={() => removePhoto(p)} disabled={!!busy} aria-label="Çıkar" title="Çıkar" className="ml-auto rounded p-1 text-gray-400 hover:text-gray-700"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}

        <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Ek talimat (ops., tümüne) — ör: zemin mermer, yumuşak gün ışığı"
          className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        <button type="button" onClick={() => void run()} disabled={!!busy || !jobs.length}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          {busy ? `Uyarlanıyor… (${photos.indexOf(busy) + 1}. foto)` : `Temaya uyarla (${jobs.length} fotoğraf)`}
        </button>
        {msg && <p className={`rounded-lg px-3 py-2 text-xs ${msg.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{msg.text}</p>}
      </div>
    </details>
  );
}
